import { EngagementStatus, EngagementType, type StudyTaskSelectDto } from '@@types/api.types';
import { DEFAULT_CONTEST_CONFIG } from '@azymmetric/shared/types/meia/context-builder.types';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { EffectSelectOption } from '@components/shared/effect-definition.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { Skeleton } from '@components/ui/skeleton';
import { errorToast, successToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { useGetEffects } from '@queries/effect.query';
import {
  useCreateMeiaTaskTechnologyEngagement,
  useDeleteMeiaTaskTechnologyEngagement,
  useGetMeiaTaskTechnologies,
  useGetMeiaTaskTechnologiesEngagements,
  useGetMeiaThreatTargetSystems,
  useUpdateMeiaTaskTechnologyEngagement,
} from '@queries/meia.query';
import {
  type MeiaTaskEngagementEntryFormValues,
  type MeiaTaskEngagementsFormValues,
  meiaTaskEngagementsSchema,
} from '@schemas/meia.schema';
import { mapToSelectOptions } from '@utils/select.util';
import { CrosshairIcon, Trash2Icon } from 'lucide-react';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { formatPkEstimate, getEngagementTypeLabel, getPkBandBadgeVariant } from './meia-p3-engagement.util';
import {
  buildEmptyEngagementEntry,
  buildEngagementEntryFromServer,
  diffEngagementEntries,
} from './meia-task-engagements.util';

const engagementTypeOptions = Object.values(EngagementType).map((value) => ({
  label: getEngagementTypeLabel(value),
  value,
}));

type MeiaTaskEngagementsFormChildren = (params: {
  body: ReactNode;
  /** Reads the form's current dirty state — for host close-guards. */
  getIsDirty: () => boolean;
  isLoading: boolean;
  isSaving: boolean;
  /** Drops unsaved edits; the host calls this when the modal closes. */
  reset: () => void;
  submit: () => void;
}) => ReactNode;

type MeiaTaskEngagementsFormProps = {
  children: MeiaTaskEngagementsFormChildren;
  /** Called once a save completes so the host can close the modal. */
  onSaved?: () => void;
  readOnly: boolean;
  studyId: string;
  task?: StudyTaskSelectDto | null;
};

/**
 * Red Target Engagement editor for the task modal: one form holding every engagement row
 * (Blue technology → Red target pairing) so the step persists through one Save in the host
 * footer. Rows are added/removed dynamically and diffed against the server on save.
 */
export const MeiaTaskEngagementsForm: FC<MeiaTaskEngagementsFormProps> = (props) => {
  const { children, onSaved, readOnly, studyId, task } = props;
  const [isSaving, setIsSaving] = useState(false);

  const { data: taskTechnologies = [] } = useGetMeiaTaskTechnologies({ studyId, taskId: task?.id });
  const { engagements: serverEngagements, isLoading } = useGetMeiaTaskTechnologiesEngagements({
    studyId,
    taskTechnologies,
  });
  const { data: effects = [] } = useGetEffects();
  const { data: threatTargetSystems = [] } = useGetMeiaThreatTargetSystems();

  const { mutateAsync: createEngagement } = useCreateMeiaTaskTechnologyEngagement();
  const { mutateAsync: updateEngagement } = useUpdateMeiaTaskTechnologyEngagement();
  const { mutateAsync: deleteEngagement } = useDeleteMeiaTaskTechnologyEngagement();

  const technologyOptions = useMemo(
    () =>
      taskTechnologies.map((taskTechnology) => ({
        label: taskTechnology.technologyName ?? taskTechnology.technologyId,
        value: taskTechnology.id,
      })),
    [taskTechnologies],
  );
  const effectOptions = useMemo(() => mapToSelectOptions(effects), [effects]);
  const targetSystemOptions = useMemo(
    () => threatTargetSystems.map((targetSystem) => ({ label: targetSystem.name, value: targetSystem.id })),
    [threatTargetSystems],
  );
  const targetSystemByCode = useMemo(
    () => new Map(threatTargetSystems.map((targetSystem) => [targetSystem.id, targetSystem])),
    [threatTargetSystems],
  );

  const buildValuesFromServer = (): MeiaTaskEngagementsFormValues => ({
    engagements: serverEngagements.map((engagement) => buildEngagementEntryFromServer(engagement)),
    removedEngagements: [],
  });

  const form = useAppForm({
    defaultValues: buildValuesFromServer(),
    onSubmit: async ({ value }) => {
      setIsSaving(true);
      try {
        const baselineById = new Map(
          serverEngagements.map((engagement) => [engagement.id, buildEngagementEntryFromServer(engagement)]),
        );
        const { creates, moves, updates } = diffEngagementEntries({ baselineById, entries: value.engagements });

        const createFromEntry = async (entry: MeiaTaskEngagementEntryFormValues): Promise<void> => {
          const created = await createEngagement({
            alternatePaths: null,
            contestConfig: DEFAULT_CONTEST_CONFIG,
            effectId: entry.effectId,
            engagementType: entry.engagementType,
            governingMOP: null,
            pkEstimate: entry.pkEstimate,
            status: EngagementStatus.DRAFT,
            studyId,
            taskTechnologyId: entry.taskTechnologyId,
            threatTargetComponentCode: entry.threatTargetComponentCode,
            threatTargetSystemCode: entry.threatTargetSystemCode,
          });
          const entryIndex = form.state.values.engagements.findIndex(
            (candidate) => candidate._localId === entry._localId,
          );
          if (entryIndex !== -1) {
            form.setFieldValue(`engagements[${entryIndex}].id`, created.id);
          }
        };

        for (const entry of creates) {
          await createFromEntry(entry);
        }

        // Technology is the engagement's parent resource, so a technology change recreates the row.
        // Create the replacement before deleting the original so a failed create never loses the row.
        for (const entry of moves) {
          const baseline = entry.id ? baselineById.get(entry.id) : undefined;
          const previousEngagementId = entry.id;
          await createFromEntry(entry);
          if (previousEngagementId && baseline) {
            await deleteEngagement({
              engagementId: previousEngagementId,
              studyId,
              taskTechnologyId: baseline.taskTechnologyId,
            });
          }
        }

        for (const entry of updates) {
          if (!entry.id) continue;
          await updateEngagement({
            effectId: entry.effectId,
            engagementId: entry.id,
            engagementType: entry.engagementType,
            pkEstimate: entry.pkEstimate,
            studyId,
            taskTechnologyId: entry.taskTechnologyId,
            threatTargetComponentCode: entry.threatTargetComponentCode,
            threatTargetSystemCode: entry.threatTargetSystemCode,
          });
        }

        for (const removed of value.removedEngagements) {
          await deleteEngagement({
            engagementId: removed.id,
            studyId,
            taskTechnologyId: removed.taskTechnologyId,
          });
        }
        if (value.removedEngagements.length > 0) {
          form.setFieldValue('removedEngagements', []);
        }

        // Rebase the baseline so the host close-guard sees a clean form after a save.
        form.reset(form.state.values);
        successToast({ description: 'Red target engagements saved.' });
        onSaved?.();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save engagements.';
        errorToast({ description: message });
      } finally {
        setIsSaving(false);
      }
    },
    onSubmitInvalid: () => {
      errorToast({ description: 'Please complete the required fields on every engagement.' });
    },
    validators: { onSubmit: meiaTaskEngagementsSchema },
  });

  const engagementsSignature = useMemo(
    () => serverEngagements.map((engagement) => engagement.id).join('|'),
    [serverEngagements],
  );

  // Rehydrate only when the server-side engagement set changes, and never while a save is in
  // flight or the form holds unsaved edits — the save loop's own invalidations change the
  // signature mid-flight, and resetting then would wipe the user's remaining input.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the structural signature + isSaving, not `serverEngagements`/`form`
  useEffect(() => {
    if (isSaving || form.state.isDirty) return;
    form.reset(buildValuesFromServer());
  }, [isSaving, engagementsSignature]);

  const submit = (): void => void form.handleSubmit();
  const reset = (): void => form.reset(buildValuesFromServer());

  const renderEngagementCard = (
    entry: MeiaTaskEngagementEntryFormValues,
    index: number,
    onRemove: () => void,
  ): JSX.Element => (
    <div
      className="flex flex-col gap-3 rounded-md border border-red-200 bg-red-50/30 p-3"
      key={entry._localId}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-red-700">Engagement #{index + 1}</span>
        {!readOnly && (
          <IconButton
            ariaLabel={`Remove engagement ${index + 1}`}
            icon={<Trash2Icon />}
            onClick={onRemove}
            variant="destructive"
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <form.AppField name={`engagements[${index}].taskTechnologyId`}>
          {(field) => (
            <field.SelectField
              disabled={readOnly}
              label="Blue Technology"
              options={technologyOptions}
              placeholder="Which attached technology engages the target?"
              required
            />
          )}
        </form.AppField>

        <form.AppField name={`engagements[${index}].effectId`}>
          {(field) => (
            <field.SelectPopoverField
              disabled={readOnly}
              emptyMessage="No effects found."
              formatValue={(option) => option?.label || 'Select effect'}
              label="Effect"
              options={effectOptions}
              placeholder="Select effect"
              renderOption={(option) => <EffectSelectOption option={option} />}
              required
              searchPlaceholder="Search effects…"
            />
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.engagements[index]?.threatTargetSystemCode}>
          {(selectedTargetCode) => {
            const components = (selectedTargetCode && targetSystemByCode.get(selectedTargetCode)?.components) || [];
            const hasComponents = components.length > 0;

            // Component picker only appears when the chosen target has catalogued components;
            // otherwise the target system field spans the full row.
            return (
              <div className={cn('grid grid-cols-1 gap-3 md:col-span-2', hasComponents && 'md:grid-cols-2')}>
                <form.AppField name={`engagements[${index}].threatTargetSystemCode`}>
                  {(field) => (
                    <field.SelectField
                      disabled={readOnly}
                      label="Red Target System"
                      onValueChange={() => {
                        // Components are target-specific; a target change invalidates the old pick.
                        form.setFieldValue(`engagements[${index}].threatTargetComponentCode`, null);
                      }}
                      options={targetSystemOptions}
                      placeholder="Which adversary system is engaged?"
                      required
                    />
                  )}
                </form.AppField>

                {hasComponents && (
                  <form.AppField name={`engagements[${index}].threatTargetComponentCode`}>
                    {(field) => (
                      <field.SelectField
                        clearable
                        disabled={readOnly}
                        label="Component"
                        options={components.map((component) => ({ label: component.name, value: component.id }))}
                        placeholder="Whole system (optional)"
                      />
                    )}
                  </form.AppField>
                )}
              </div>
            );
          }}
        </form.Subscribe>

        <form.AppField name={`engagements[${index}].engagementType`}>
          {(field) => (
            <field.SelectField
              clearable
              disabled={readOnly}
              label="Engagement Type"
              options={engagementTypeOptions}
              placeholder="Mechanism, e.g. Jam (optional)"
            />
          )}
        </form.AppField>

        <div className="flex flex-col gap-1.5">
          <form.AppField name={`engagements[${index}].pkEstimate`}>
            {(field) => (
              <field.TextField
                disabled={readOnly}
                label="pK"
                max={1}
                min={0}
                placeholder="0.70"
                step={0.01}
                tooltip="Probability of kill — the estimated likelihood (0 to 1) that this engagement neutralizes the target. Higher pK drives a stronger model Effect-on-Target status."
                type="number"
              />
            )}
          </form.AppField>
          <form.Subscribe selector={(state) => state.values.engagements[index]?.pkEstimate}>
            {(pkValue) => {
              if (pkValue === null || pkValue === undefined || Number.isNaN(pkValue)) return null;

              return (
                <Badge
                  className="self-start"
                  size="xs"
                  variant={getPkBandBadgeVariant(pkValue)}
                >
                  {formatPkEstimate(pkValue)}
                </Badge>
              );
            }}
          </form.Subscribe>
        </div>
      </div>
    </div>
  );

  const body =
    !task || isLoading ? (
      <Skeleton className="h-40 w-full" />
    ) : taskTechnologies.length === 0 ? (
      <EmptyStateContainer
        className="min-h-[260px] rounded-md border border-dashed border-border text-center"
        message="Attach technologies in the Technologies step — each engagement binds a Blue technology to the Red target it engages."
        title="No Engaged Technologies"
      />
    ) : (
      <form
        {...MEIA_AUTOFILL_IGNORE_PROPS}
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          submit();
        }}
      >
        <form.AppForm>
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
            <CrosshairIcon className="size-4 text-red-600" />
            Red Target Engagement
            <TooltipComponent>
              Record which adversary target each of this task&rsquo;s technologies engages — target system, optional
              component, engagement type, pK estimate, and doctrinal effect. Engagements feed the Effect on Target views
              in P3 and the Decision Maker Brief.
            </TooltipComponent>
          </div>

          <form.AppField
            mode="array"
            name="engagements"
          >
            {(engagementArrayField) => {
              const entries = engagementArrayField.state.value ?? [];

              return (
                <div className="flex flex-col gap-3">
                  {entries.length === 0 && (
                    <p className="text-xs text-gray-400">
                      No red target engagements yet — add one to bind a technology to the target it engages.
                    </p>
                  )}

                  {entries.map((entry, index) =>
                    renderEngagementCard(entry, index, () => {
                      if (entry.id) {
                        form.setFieldValue('removedEngagements', (current) => [
                          ...current,
                          { id: entry.id ?? '', taskTechnologyId: entry.taskTechnologyId },
                        ]);
                      }
                      engagementArrayField.removeValue(index);
                    }),
                  )}

                  {!readOnly && (
                    <Button
                      className="self-start"
                      onClick={() =>
                        engagementArrayField.pushValue(
                          buildEmptyEngagementEntry({
                            taskTechnologyId: taskTechnologies.length === 1 ? taskTechnologies[0].id : undefined,
                          }),
                        )
                      }
                      type="button"
                      variant="link"
                    >
                      + Add Red Target Engagement
                    </Button>
                  )}
                </div>
              );
            }}
          </form.AppField>
        </form.AppForm>
      </form>
    );

  return <>{children({ body, getIsDirty: () => form.state.isDirty, isLoading, isSaving, reset, submit })}</>;
};
